import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { DEFAULT_PARAMS } from '@/model/defaults';
import type { AdminClient, AdminScenario, AdminSession } from '@/persistence/adminClient';
import { isValidAccessCode, normalizeAccessCode } from '@/routes/accessCode';

/**
 * Every provisioned scenario, and the form that adds one.
 *
 * Until this existed, a new access code could only be brought into being by a
 * hand-written INSERT through a production database shell.
 */
type LoadState = 'loading' | 'ready' | 'unavailable';
type CreateState =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'invalid' }
  | { kind: 'conflict'; code: string }
  | { kind: 'unavailable' }
  | { kind: 'created'; code: string };

/** Local to the admin table; the history panel formats its own dates. */
const WHEN = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' });
const formatWhen = (iso: string) => {
  const parsed = Date.parse(iso);
  return Number.isNaN(parsed) ? '—' : WHEN.format(new Date(parsed));
};

interface Props {
  client: AdminClient;
  session: AdminSession;
  onOpen: (code: string) => void;
  /** The token stopped being accepted: lock back to the gate rather than retry. */
  onExpired: () => void;
}

export function AdminScenarioList({ client, session, onOpen, onExpired }: Props) {
  const [state, setState] = useState<LoadState>('loading');
  const [scenarios, setScenarios] = useState<AdminScenario[]>([]);
  const [newCode, setNewCode] = useState('');
  const [create, setCreate] = useState<CreateState>({ kind: 'idle' });

  const refresh = useCallback(async () => {
    const result = await client.listScenarios(session.token);
    if (result.status === 'unauthorized') {
      onExpired();
      return;
    }
    if (result.status === 'unavailable') {
      setState('unavailable');
      return;
    }
    setScenarios(result.scenarios);
    setState('ready');
  }, [client, session.token, onExpired]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (create.kind === 'saving') return;

    /* The same validator the prospect gate uses, which the database's CHECK
       constraint also mirrors — so a bad code is caught here rather than as a
       400 from three layers down. */
    const code = normalizeAccessCode(newCode);
    if (!isValidAccessCode(code)) {
      setCreate({ kind: 'invalid' });
      return;
    }

    setCreate({ kind: 'saving' });
    /* Seeded with the repo defaults. They travel from here because the service
       keeps no runtime copy of the model — see the drift guard in schema.ts. */
    const result = await client.createScenario(session.token, code, DEFAULT_PARAMS);

    switch (result.status) {
      case 'created':
        setNewCode('');
        setCreate({ kind: 'created', code });
        await refresh();
        return;
      case 'conflict':
        setCreate({ kind: 'conflict', code });
        return;
      case 'unauthorized':
        onExpired();
        return;
      case 'unavailable':
        setCreate({ kind: 'unavailable' });
        return;
    }
  };

  return (
    <main className="canvas admin">
      <header className="admin__header">
        <h1>Scenarios</h1>
        <p className="admin__hint">
          {state === 'ready'
            ? `${scenarios.length} provisioned. Open one to adjust the numbers it shows.`
            : 'Every provisioned access code.'}
        </p>
      </header>

      <form className="admin-create" onSubmit={submit}>
        <label className="admin-create__field" htmlFor="new-code">
          <span className="sr-only">New access code</span>
          <input
            id="new-code"
            type="text"
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            placeholder="new-code"
            value={newCode}
            onChange={(event) => {
              setNewCode(event.target.value);
              setCreate({ kind: 'idle' });
            }}
          />
        </label>
        <button type="submit" disabled={create.kind === 'saving'}>
          {create.kind === 'saving' ? 'Creating…' : 'Create scenario'}
        </button>
      </form>

      {create.kind === 'invalid' && (
        <p className="gate-error" role="alert">
          Lowercase letters, numbers and hyphens only, three characters or more.
        </p>
      )}
      {create.kind === 'conflict' && (
        <p className="gate-error" role="alert">
          {create.code} already exists.
        </p>
      )}
      {create.kind === 'unavailable' && (
        <p className="gate-error" role="alert">
          Can’t reach the admin service, so nothing was created.
        </p>
      )}
      {create.kind === 'created' && (
        <p className="admin__created" role="status">
          Created {create.code}, seeded with the default assumptions.
        </p>
      )}

      {state === 'loading' && (
        <p className="loading" role="status">
          Loading scenarios…
        </p>
      )}

      {state === 'unavailable' && (
        <div className="gate-notice" role="status">
          <h1>Can’t reach the admin service</h1>
          <p>The scenario list is unavailable, so nothing is shown rather than an empty one.</p>
          <button type="button" className="gate-notice__action" onClick={() => void refresh()}>
            Try again
          </button>
        </div>
      )}

      {state === 'ready' && (
        <table className="admin-table">
          <thead>
            <tr>
              <th scope="col">Access code</th>
              <th scope="col">Version</th>
              <th scope="col">Revisions</th>
              <th scope="col">Last edited</th>
              <th scope="col">
                <span className="sr-only">Open</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {scenarios.map((scenario) => {
              const deleted = scenario.deletedAt !== null;
              return (
                <tr key={scenario.accessCode} className={deleted ? 'is-deleted' : undefined}>
                  <td>
                    <code>{scenario.accessCode}</code>
                    {deleted && <span className="admin-table__tag">deleted</span>}
                  </td>
                  <td>{scenario.version}</td>
                  <td>{scenario.revisions}</td>
                  <td>{formatWhen(scenario.updatedAt)}</td>
                  <td>
                    {/* Deliberately not openable. A write revives a soft-deleted
                        code, so nudging one number here would silently
                        un-delete a scenario someone retired on purpose. */}
                    {deleted ? (
                      <span className="admin-table__note">retired</span>
                    ) : (
                      <button type="button" onClick={() => onOpen(scenario.accessCode)}>
                        Open
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </main>
  );
}
